import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, message, Popconfirm, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { PoaStatus, RetrievePoaSchema } from '@app/models/users';
import { formatDate } from '@app/services/date';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { AccessDenied } from '@components/AccessDenied';

import { usePoasQuery, useUpdatePoaMutation } from '../../hooks/usePoaQueries';

import { PoaModal } from './components/PoaModal';
import { IPoaTarget } from './interfaces';
import { formatSnils } from './services';

interface IProps {
  target: IPoaTarget;
}

export const Poas: React.FC<IProps> = ({ target }) => {
  const scope = useLocalSelector((state) => state.currentSession.data?.scope);
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const canView = !!(target.kind === 'legalEntity' ? scope?.$permissions.view : scope?.$permissions.view_coalition);
  const canManage = !!(target.kind === 'legalEntity'
    ? scope?.$permissions.update
    : scope?.$permissions.manage_coalition);
  const query = usePoasQuery(target, canView && !scope?.is_sandbox);
  const update = useUpdatePoaMutation(target);
  const [open, setOpen] = React.useState(false);
  const [edited, setEdited] = React.useState<RetrievePoaSchema>();

  const change = (uid: string, data: { is_primary?: true; status?: 'revoked' }) =>
    update.mutate(
      { uid, data },
      {
        onSuccess: () => {
          message.success(commonT.t('dataPatchSuccess'));
        },
        onError: (err) => {
          message.error(getErrorMessage(err));
        },
      }
    );

  const columns: ColumnsType<RetrievePoaSchema> = [
    { key: 'number', dataIndex: 'number', title: spaceT.t('poa.number') },
    {
      key: 'internal_number',
      dataIndex: 'internal_number',
      title: spaceT.t('poa.internalNumber'),
      render: (value) => value || spaceT.t('poa.noNumber'),
    },
    {
      key: 'representative',
      title: spaceT.t('poa.representative'),
      render: (_, row) =>
        [row.representative_last_name, row.representative_first_name, row.representative_middle_name]
          .filter(Boolean)
          .join(' '),
    },
    { key: 'representative_inn', dataIndex: 'representative_inn', title: commonT.t('inn') },
    {
      key: 'representative_snils',
      dataIndex: 'representative_snils',
      title: spaceT.t('poa.snils'),
      render: formatSnils,
    },
    {
      key: 'representative_position',
      dataIndex: 'representative_position',
      title: spaceT.t('poa.position'),
      render: (value) => value || spaceT.t('poa.authorized'),
    },
    { key: 'issued_on', dataIndex: 'issued_on', title: spaceT.t('poa.issuedOn'), render: (value) => formatDate(value) },
    {
      key: 'valid_until',
      dataIndex: 'valid_until',
      title: spaceT.t('poa.validUntil'),
      render: (value) => formatDate(value),
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: spaceT.t('poa.status'),
      render: (value: PoaStatus) => (
        <Tag color={value === PoaStatus.Active ? 'success' : value === PoaStatus.Revoked ? 'error' : 'default'}>
          {spaceT.t(`poa.statuses.${value}` as const)}
        </Tag>
      ),
    },
    {
      key: 'is_effective',
      dataIndex: 'is_effective',
      title: spaceT.t('poa.effective'),
      render: (value) => commonT.t(value ? 'yes' : 'no'),
    },
    {
      key: 'is_primary',
      dataIndex: 'is_primary',
      title: spaceT.t('poa.primary'),
      render: (value) => commonT.t(value ? 'yes' : 'no'),
    },
    ...(canManage
      ? [
          {
            key: 'action',
            title: '',
            render: (_: unknown, row: RetrievePoaSchema) =>
              row.status !== PoaStatus.Revoked && (
                <Space>
                  <Button
                    type="link"
                    size="small"
                    disabled={update.isLoading}
                    onClick={() => {
                      setEdited(row);
                      setOpen(true);
                    }}
                  >
                    {commonT.t('edit')}
                  </Button>
                  {!row.is_primary && (
                    <Button
                      type="link"
                      size="small"
                      loading={update.isLoading && update.variables?.uid === row.uid}
                      disabled={update.isLoading}
                      onClick={() => change(row.uid, { is_primary: true })}
                    >
                      {spaceT.t('poa.makePrimary')}
                    </Button>
                  )}
                  <Popconfirm
                    title={spaceT.t('poa.revokeConfirm')}
                    okText={commonT.t('confirm')}
                    cancelText={commonT.t('cancel')}
                    disabled={update.isLoading}
                    onConfirm={() => change(row.uid, { status: 'revoked' })}
                  >
                    <Button type="link" size="small" danger disabled={update.isLoading}>
                      {spaceT.t('poa.revoke')}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
          },
        ]
      : []),
  ];

  if (!canView) {
    return <AccessDenied withProfileLink={false} />;
  }

  return (
    <>
      {scope?.is_sandbox ? (
        <Alert type="warning" showIcon title={spaceT.t('organization.sandboxTitle')} />
      ) : query.isError ? (
        <Alert
          type="error"
          showIcon
          title={getErrorMessage(query.error)}
          action={
            <Button size="small" onClick={() => query.refetch()}>
              {commonT.t('buttonReload')}
            </Button>
          }
        />
      ) : (
        <Flex vertical gap={16}>
          {canManage && (
            <Flex justify="flex-end">
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  setEdited(undefined);
                  setOpen(true);
                }}
              >
                {commonT.t('buttonAdd')}
              </Button>
            </Flex>
          )}
          <Table<RetrievePoaSchema>
            rowKey="uid"
            className="tableClassname"
            loading={query.isInitialLoading}
            columns={columns}
            dataSource={query.data}
            pagination={false}
            scroll={{ x: true }}
          />
          <PoaModal open={open} target={target} poa={edited} onClose={() => setOpen(false)} />
        </Flex>
      )}
    </>
  );
};
